import React, { useRef, useState } from "react";
import { Box, Button, LoadingOverlay, Tabs, Text } from "@mantine/core";
import { useStaffFormSalon } from "./useStaffFormSalon";
import { useResponsive } from "../../../context/ResponsiveContext";
import usePermissions from "../../../hooks/usePermissions";
import type {
  SavedStaffMember,
  StaffFormInitialData,
  StaffFormSaveResult,
  StaffFormSection,
} from "../../../types/staffForm";
import { DisableCalendarConfirmModal } from "./DisableCalendarConfirmModal";
import { GeneralInfoPanel } from "./GeneralInfoPanel";
import { HoursPanel } from "./HoursPanel";
import { MenuPanel } from "./MenuPanel";
import { buildRoleOptions, needsDisableConfirm } from "./staffFormUtils";
import { useStaffForm } from "./useStaffForm";
import { useMenuServices } from "./useMenuServices";
import { useStaffFormSubmit } from "./useStaffFormSubmit";
import { useStaffPhoto } from "./useStaffPhoto";
import styles from "./StaffForm.module.css";

const SECTION_LABELS: Record<StaffFormSection, string> = {
  general: "General Info",
  hours: "Hours",
  menu: "Menu",
};

const READ_ONLY_NO_PERMISSION =
  "You don't have permission to change service assignments.";
const READ_ONLY_INACTIVE =
  "Inactive staff can't be assigned services. Set their status to Active to edit their menu.";

export interface StaffFormProps {
  /** The staff member being edited; omit to add a new one. */
  initialData?: StaffFormInitialData | null;
  /**
   * After each successful staff write (invite or update) — even if a later
   * service write fails — so the host's staff list never misses a new member.
   */
  onStaffSaved: (staff: SavedStaffMember, meta: { isCreate: boolean }) => void;
  /** After the whole save (staff + services) succeeded. */
  onComplete: (result: StaffFormSaveResult) => void | Promise<void>;
  /** Reports the in-flight save so a host modal can lock closing. */
  onSavingChange?: (saving: boolean) => void;
  /**
   * Show the Menu tab (default). Onboarding hides it: services are assigned
   * later, and a hidden Menu never writes staff_services.
   */
  showMenu?: boolean;
}

/**
 * The staff create/edit form: General Info, Hours and Menu tabs with a single
 * Save. Shared by Settings → Staff and the /add-employees page.
 */
export function StaffForm({
  initialData,
  onStaffSaved,
  onComplete,
  onSavingChange,
  showMenu = true,
}: StaffFormProps) {
  const { businessInfo, currentUser } = useStaffFormSalon();
  const { isMobile } = useResponsive();
  const perms = usePermissions();
  const staff = useStaffForm(initialData);
  const { form } = staff;
  const values = form.getValues();
  const photo = useStaffPhoto(initialData);

  const servicePerms = perms?.settingsServices;
  const canViewMenu = showMenu && servicePerms?.available !== false;
  const menuServices = useMenuServices(canViewMenu);
  const canEditMenu = canViewMenu && servicePerms?.canManageServices !== false;
  // The backend refuses new assignments for Inactive staff.
  const menuReadOnlyReason = !canEditMenu
    ? READ_ONLY_NO_PERMISSION
    : values.status === "Inactive"
      ? READ_ONLY_INACTIVE
      : null;

  const { submit, staffExists } = useStaffFormSubmit({
    initialData,
    touchedHourDays: staff.touchedHourDays,
    userToggledBookable: staff.userToggledBookable,
    photo: { change: photo.change, markSaved: photo.markSaved },
    baselineAssignments: staff.baselineAssignments,
    canWriteMenu: canViewMenu && !menuReadOnlyReason,
    onStaffSaved,
  });

  const [activeSection, setActiveSection] =
    useState<StaffFormSection>("general");
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [confirmDisableOpen, setConfirmDisableOpen] = useState(false);
  const panelsRef = useRef<HTMLDivElement>(null);

  const sections: StaffFormSection[] = canViewMenu
    ? ["general", "hours", "menu"]
    : ["general", "hours"];

  const showSection = (section: StaffFormSection) => {
    setActiveSection(section);
    panelsRef.current?.scrollTo({ top: 0 });
  };

  const save = async () => {
    setIsSaving(true);
    onSavingChange?.(true);
    const outcome = await submit(form.getValues());
    setIsSaving(false);
    onSavingChange?.(false);
    if (outcome.ok) {
      await onComplete(outcome.result);
      return;
    }
    setFormError(outcome.message);
    showSection(outcome.section ?? activeSection);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);
    const [firstIssue] = staff.validate();
    if (firstIssue) {
      // Field issues render on their inputs; others need the form-level line.
      if (!firstIssue.field) setFormError(firstIssue.message);
      showSection(firstIssue.section);
      return;
    }
    if (
      needsDisableConfirm({
        initialData,
        wantsBookable: form.getValues().hasBookableCalendar,
      })
    ) {
      setConfirmDisableOpen(true);
      return;
    }
    void save();
  };

  return (
    <>
      <Box
        component="form"
        noValidate
        onSubmit={handleSubmit}
        className={styles.root}
      >
        <LoadingOverlay visible={isSaving} overlayProps={{ blur: 2 }} />
        <Tabs
          value={activeSection}
          onChange={(value) => value && showSection(value as StaffFormSection)}
          variant="pills"
          keepMounted={false}
          classNames={{
            root: styles.tabs,
            list: styles.tabTrack,
            tab: styles.tabSegment,
          }}
        >
          <Tabs.List grow aria-label="Staff member sections">
            {sections.map((section) => (
              <Tabs.Tab key={section} value={section}>
                {SECTION_LABELS[section]}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          <Box ref={panelsRef} className={styles.panels}>
            {formError && (
              <Text c="red" size="sm" mb="md" role="alert">
                {formError}
              </Text>
            )}

            <Tabs.Panel value="general">
              <GeneralInfoPanel
                form={form}
                isCreate={staff.isCreate}
                isSelf={!!initialData?.isSelf}
                roleOptions={buildRoleOptions(
                  currentUser?.role,
                  initialData?.role,
                )}
                onRoleChange={staff.setRole}
                photo={{
                  url: photo.displayUrl,
                  error: photo.error,
                  onSelectFile: photo.selectFile,
                  onRemove: photo.remove,
                }}
              />
            </Tabs.Panel>

            <Tabs.Panel value="hours">
              <HoursPanel
                hasBookableCalendar={values.hasBookableCalendar}
                onBookableChange={staff.setBookable}
                hours={values.hours}
                onToggleDay={staff.toggleDay}
                onTimeChange={staff.setDayTime}
                staffId={initialData?.id}
                businessLocationId={businessInfo?.id}
              />
            </Tabs.Panel>

            {canViewMenu && (
              <Tabs.Panel value="menu">
                <MenuPanel
                  services={menuServices.services}
                  loadFailed={menuServices.loadFailed}
                  onRetryLoad={() => void menuServices.retry()}
                  assignments={values.serviceAssignments}
                  onAssignmentChange={staff.setAssignment}
                  readOnlyReason={menuReadOnlyReason}
                  hasBookableCalendar={values.hasBookableCalendar}
                  onGoToHours={() => showSection("hours")}
                  showServicesLink={canEditMenu}
                  isMobile={isMobile}
                />
              </Tabs.Panel>
            )}
          </Box>
        </Tabs>

        <Box className={styles.footer}>
          <Button
            type="submit"
            fullWidth
            color="teal"
            size={isMobile ? "lg" : "md"}
            disabled={isSaving}
          >
            {staffExists ? "Save Changes" : "Add New Employee"}
          </Button>
        </Box>
      </Box>

      <DisableCalendarConfirmModal
        opened={confirmDisableOpen}
        onCancel={() => setConfirmDisableOpen(false)}
        onConfirm={() => {
          setConfirmDisableOpen(false);
          void save();
        }}
      />
    </>
  );
}
